import debounce from 'lodash/debounce';
import React, { ChangeEvent, useState, useEffect, useMemo } from 'react';

import { useTextContext } from 'Contexts/textContext';
import { Paragraph } from 'DesignLibrary/atoms';
import { DateOfBirthContainer } from 'DesignLibrary/atoms/inputs/DateOfBirthInput/styled';
import TextInput from 'DesignLibrary/atoms/inputs/TextInput';

export interface DateOfBirthInputProps {
  stretch: boolean;
  value?: string;
  onChange: (dob: string) => void;
  error: boolean;
  disabled?: boolean;
}

export const isValidDob = (value: string) => {
  // Ensure the input matches the expected AA/BB/YYYY format
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false;

  const [mm, dd, year] = value.split('/').map(Number);

  // Date months are zero-based, so subtract 1 from the input month
  const date = new Date(year, mm - 1, dd);

  // Verify that the Date object represents the same day, month, and year as input
  // Add 1 to date.getMonth() because getMonth() is zero-based
  if (date.getFullYear() !== year || date.getMonth() + 1 !== mm || date.getDate() !== dd) {
    return false;
  }

  // Valid if age is between 0 and 120 years
  const age = new Date().getFullYear() - year;
  const valid = age >= 0 && age <= 120;

  return valid;
};

// Convert API (MM/DD/YYYY) → locale display
const toLocaleFormat = (apiValue: string, locale: string): string => {
  if (!apiValue) return '';
  const [mm, dd, yyyy] = apiValue.split('/');
  return locale === 'en-US' ? apiValue : [dd, mm, yyyy].join('/');
};

// Convert locale display → API (MM/DD/YYYY)
const toUSFormat = (displayValue: string, locale: string): string => {
  const parts = displayValue.split('/');
  if (parts.length !== 3) return displayValue;
  const [a, b, c] = parts;
  return locale === 'en-US' ? displayValue : [b, a, c].join('/');
};

export const DateOfBirthInput = ({
  stretch,
  value = '',
  onChange,
  error = false,
  disabled = false,
}: DateOfBirthInputProps) => {
  const { retrieveContentfulData, locale } = useTextContext();
  // State for the input shown to the user, in locale format
  const [displayValue, setDisplayValue] = useState(() => toLocaleFormat(value, locale));
  // Used to prevent error state flickering
  const [isFormatting, setIsFormatting] = useState(false);

  // Keep displayValue in sync when locale changes
  useEffect(() => {
    setDisplayValue(toLocaleFormat(value, locale));
  }, [locale]);

  // Debounce API updates to prevent flicker
  const debouncedOnChange = useMemo(
    () =>
      debounce((apiVal: string) => {
        onChange(apiVal);
        setIsFormatting(false);
      }, 300),
    [onChange],
  );
  // Cleanup on unmount
  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange]);

  const handleChange = (_: ChangeEvent<HTMLInputElement>, raw?: number | string | null) => {
    const text = raw?.toString() ?? '';
    // Strip non-digits and limit to 8
    const digits = text.replace(/\D/g, '').slice(0, 8);
    // Build locale-formatted display
    const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
    const formatted = parts.join('/');
    setDisplayValue(formatted);
    setIsFormatting(true);
    // Convert back to API format (MM/DD/YYYY) and debounce
    const apiValue = toUSFormat(formatted, locale);
    debouncedOnChange(apiValue);
  };

  const errorText = retrieveContentfulData<string>('profile_section.error_text.invalidDateOfBirthInput');
  const placeholder = retrieveContentfulData<string>('profile_section.claims.date_of_birth_placeholder');
  const digitsOnly = displayValue.replace(/\D/g, '');
  const entryComplete = digitsOnly.length === 8;
  const showError = error && entryComplete && !isFormatting;

  return (
    <>
      <DateOfBirthContainer>
        <TextInput
          data-testid="dob-input"
          stretch={stretch}
          inputType="normal"
          inputMode="numeric"
          placeholder={placeholder}
          value={displayValue}
          handleChange={handleChange}
          error={showError}
          disabled={disabled}
        />
      </DateOfBirthContainer>
      {showError && (
        <Paragraph size="mini" color="--primary-red" data-testid="label-error">
          {errorText}
        </Paragraph>
      )}
    </>
  );
};
